/**
 * The report page: choose what goes in one, see it, buy it, and get it back.
 *
 * It is deliberately **independent of the dashboard**. It picks its own county,
 * its own scope and its own filters rather than inheriting whatever table
 * happened to be on screen, because a report is a document somebody sets out to
 * produce — not a side effect of where they were browsing. A link from the
 * dashboard still carries its filters through, and they arrive here as a
 * starting point that can be changed.
 *
 * Five decisions here are product decisions rather than layout.
 *
 * 1. **The basket lives in the URL.** Every control mirrors into the query
 *    string, so a price survives a reload and a Back, and can be sent to
 *    somebody else and still be the same price. `replaceSearch` rather than a
 *    push, or Back would walk through a hundred intermediate baskets.
 * 2. **Nothing is priced in the browser.** The counts, the rates, the credit
 *    balance and the total all come back from `/api/reports/quote`. A count
 *    computed here would be a price computed here, and the server would then
 *    have to either trust it or contradict it.
 * 3. **The preview is the document, not a picture of it.** It is the same
 *    renderer the PDF goes through, capped and labelled. Laying rows out here
 *    would be a second description of the report that can promise what the
 *    file does not deliver.
 * 4. **What the county can evidence is shown beside what it costs.** A buyer
 *    comparing $48 in Broward with $48 in Pasco is comparing two different
 *    products and the total does not say so. `scoreScope.ts`' argument, at the
 *    point where money changes hands.
 * 5. **A generated report is kept and listed.** The counties rewrite these
 *    registers continuously, so the document cannot be produced again — which
 *    makes the archive below the only copy, and deleting one genuinely
 *    destructive in a way nothing else in this product is.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import type { County, Quote, ReportRecord, ScoringRules } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useDebounced } from "../hooks/useDebounced";
import { useScoring } from "../hooks/useScoring";
import { formatWhen } from "../lib/alerts";
import {
  depthNote,
  ceilingNote,
  dueSummary,
  lineSummary,
  money,
  reportReadiness,
} from "../lib/pricing";
import {
  fromQuery,
  missingChoice,
  reportableCounties,
  keepAnswerable,
  resolveScope,
  scopeOptions,
  signalOptions,
  toQuery,
  toSearch,
  type ReportParams,
  type ReportScope,
} from "../lib/reportParams";
import { alreadyRecorded, track } from "../lib/analytics";
import { purchaseReturn, reportState } from "../lib/reportStatus";
import { scoreLegend } from "../lib/scoreLegend";
import { DASHBOARD_PATH } from "../lib/route";
import { Link, replaceSearch } from "../router";


const RATE_OPTIONS = [
  { value: 0, label: "Any" },
  { value: 0.1, label: "10%+" },
  { value: 0.25, label: "25%+" },
  { value: 0.5, label: "50%+" },
];

interface Props {
  /** The query string this page was opened with, straight off the URL. */
  params: Record<string, string>;
}

export function ReportsPage({ params }: Props) {
  const { user } = useAuth();
  const [chosen, setChosen] = useState<ReportParams>(() => fromQuery(params));
  const [counties, setCounties] = useState<County[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState("");
  const [archive, setArchive] = useState<ReportRecord[] | null>(null);
  const [archiveError, setArchiveError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [note, setNote] = useState("");
  // Kept apart from `note`, which belongs to the last thing this page did.
  // Returning from Stripe is a thing that happened *elsewhere*, and collapsing
  // the two would let a later generate quietly erase the only notice a buyer
  // gets that their payment is on its way.
  const [purchaseNote, setPurchaseNote] = useState("");
  // The scoring vocabulary and its weights, from the same constants the
  // pipeline scored with. Never a local table: a label written here would be a
  // second name for a signal, and a weight written here could disagree with
  // the number printed in the document. Through the shared hook rather than a
  // fetch of its own, so however many places on this page need it there is one
  // request for the app.
  const rules = useScoring();
  const signalLabels = useMemo(
    () => Object.fromEntries((rules?.signals ?? []).map((s) => [s.flag, s.label])),
    [rules],
  );

  // A County subscriber reads their own county whatever the URL says, so the
  // chooser is fixed to it rather than offering counties the server refuses.
  // A quote is *not* county-gated — pricing somebody else's county is the
  // product — but generating one reads gated rows, so the two differ here.
  const locked = user?.counties.limit === 1 ? (user.counties.held ?? "") : "";
  const county = locked || chosen.county;
  const entry = counties.find((candidate) => candidate.fips === county);
  const scope = resolveScope(chosen.scope, entry);

  const settled = useDebounced(
    useMemo(() => ({ ...chosen, county, scope }), [chosen, county, scope]),
  );
  const missing = missingChoice(settled);
  const key = JSON.stringify(toQuery(settled));

  // The basket in the address bar, so the price is shareable. Replace rather
  // than push — see the module docstring.
  useEffect(() => {
    replaceSearch(toSearch(settled));
  }, [settled]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .counties(controller.signal)
      .then((response) => setCounties(response.counties))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const loadArchive = useCallback(async (signal?: AbortSignal) => {
    try {
      const list = await api.reports(signal);
      setArchive(list.reports);
      setArchiveError("");
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setArchive([]);
      setArchiveError(
        cause instanceof ApiError ? cause.message : "Couldn't load your reports.",
      );
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadArchive(controller.signal);
    return () => controller.abort();
  }, [loadArchive]);

  /**
   * Coming back from Stripe, which is not the same as having been paid.
   *
   * The redirect is the buyer's browser and the release is a webhook, and the
   * two race — so this re-reads the list rather than trusting the parameter,
   * and `purchaseReturn` says the payment is landing rather than that the
   * document is ready. The row itself is what eventually says that.
   *
   * The parameter leaves the address bar on its own: the basket effect above
   * writes `toSearch(settled)`, which emits basket keys and nothing else. That
   * matters for `App.tsx`'s reason — left in the URL, a reload or a shared
   * link would replay a notice about a payment that happened once — and it
   * cannot re-fire this effect, because `params` is the prop the router
   * resolved and `replaceState` does not re-resolve it.
   */
  const purchase = params.purchase ?? "";
  const purchaseSession = params.session ?? "";
  const purchaseAmount = params.amount ?? "";
  useEffect(() => {
    if (!purchase) return;
    setPurchaseNote(purchaseReturn(purchase));
    // Told to the ad platforms once per Stripe session, and only on `done`:
    // a cancelled checkout is not a sale, and a reload of the return URL is
    // not a second one. Analytics only — the document is released by the
    // webhook, whatever this URL says.
    if (purchase === "done" && purchaseSession &&
        !alreadyRecorded(purchaseSession)) {
      track({
        name: "purchase",
        kind: "report",
        amountCents: Number(purchaseAmount) || 0,
        transactionId: purchaseSession,
      });
    }
    void loadArchive();
  }, [purchase, purchaseSession, purchaseAmount, loadArchive]);

  useEffect(() => {
    if (missing) {
      setQuote(null);
      setQuoteError("");
      return;
    }
    const controller = new AbortController();
    setQuoteError("");
    setQuote(null);
    api
      .quote(toQuery(settled), controller.signal)
      .then(setQuote)
      .catch((cause: unknown) => {
        // An abort is "I asked something else", not a failure, and must stay
        // distinguishable from one.
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setQuoteError(
          cause instanceof ApiError ? cause.message : "Couldn't price this.",
        );
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, missing]);

  const readiness = reportReadiness(quote);
  const set = <K extends keyof ReportParams>(field: K, value: ReportParams[K]) =>
    setChosen((previous) => {
      const next = { ...previous, [field]: value };
      // Switching a parcels report to buildings would otherwise carry an
      // unfilterable signal along and turn the whole page into a 400 nobody
      // asked for. Safe to drop *here* and only here: the selection is on
      // screen, so a removed chip is seen.
      if (field === "scope") {
        next.signals = keepAnswerable(next.signals, next.scope);
      }
      return next;
    });

  /**
   * Build the report, and go and pay for it where there is anything to pay.
   *
   * The document is built *before* the money is taken — the counties rewrite
   * these registers on every pull, so a basket priced at ten and paid at ten
   * past could otherwise produce a different document from the one that was
   * priced. So this call always produces a report; what varies is whether it
   * comes back released or held behind a Checkout Session.
   *
   * An amount due is therefore a **checkout, not an error**. `generating` is
   * deliberately left set on the redirect branch: the page is navigating away,
   * and re-enabling the button would invite a second click that builds and
   * holds a second document nobody asked for.
   */
  async function generate() {
    setGenerating(true);
    setGenerateError("");
    setNote("");
    try {
      const created = await api.generateReport(toQuery(settled));
      if (created.checkout_url) {
        track({ name: "begin_checkout", tier: "report" });
        window.location.href = created.checkout_url;
        return;
      }
      await loadArchive();
      setNote(
        created.has_pdf
          ? `Report ready — ${created.record_count.toLocaleString()} records.`
          : `Report recorded, but this deployment has no PDF engine, so no ` +
            `document was stored.`,
      );
    } catch (cause) {
      setGenerateError(
        cause instanceof ApiError ? cause.message : "Couldn't generate that.",
      );
    } finally {
      setGenerating(false);
    }
  }

  return (
    <main className="reportpage">
      <header className="reporthead">
        <h1>Reports</h1>
        <p className="sub">
          A report is one county, as it stood at that county's last pull, with
          the age of every source printed on it. Choose what goes in it below.
        </p>
        <Link
          to={`${DASHBOARD_PATH}${county ? `?county=${county}` : ""}`}
          className="btn ghost"
        >
          Back to the dashboard
        </Link>
        {/* Stripe returns to the top of this page and the report it concerns
            is at the bottom, so the notice goes here and names where to look.
            It is never a claim that the document is ready — see
            `purchaseReturn`. */}
        {purchaseNote && <p className="returnnote">{purchaseNote}</p>}
      </header>

      {/* Filters across the top rather than down the side.
          
          A rail beside a document is the dashboard's shape, and this is not a
          dashboard: the filters are set once, up front, and then the reader
          spends the rest of the page looking at what they produced. Alongside,
          they competed with the document for width and pushed the preview into
          a column narrower than the page it renders. */}
      <section className="reportfilters">
        <div className="filtergrid">
          <label className="field">
            <span>County</span>
            <select
              value={county}
              disabled={Boolean(locked)}
              title={
                locked
                  ? "Your plan covers one county. Reports follow it."
                  : undefined
              }
              onChange={(event) => set("county", event.target.value)}
            >
              <option value="">Choose a county…</option>
              {reportableCounties(counties).map((option) => (
                <option key={option.fips} value={option.fips}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <div className="field">
            <span>Records</span>
            {/* Disabled rather than hidden, with the reason in text as well as
                the title: a county that publishes no buildings must say so,
                and an option that vanishes reads as a feature we lack. */}
            <div className="scopepick" role="group" aria-label="Records">
              {scopeOptions(entry).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="scopeopt"
                  aria-pressed={scope === option.value}
                  disabled={option.disabled}
                  title={option.reason || undefined}
                  onClick={() => set("scope", option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <label className="field">
            <span>Address, owner or folio</span>
            <input
              value={chosen.search}
              placeholder="e.g. NE 2 AVE"
              onChange={(event) => set("search", event.target.value)}
            />
          </label>

          <label className="field narrow">
            <span>Min score</span>
            <input
              type="number"
              min={0}
              step={5}
              value={chosen.min_score}
              onChange={(event) =>
                set("min_score", Number(event.target.value) || 0)
              }
            />
          </label>

          {scope !== "units" && (
            <label className="field narrow">
              <span>Min distress rate</span>
              <select
                value={chosen.min_rate}
                onChange={(event) => set("min_rate", Number(event.target.value))}
              >
                {RATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* These two narrow *further* — they are ANDed with the signal
              chips below, which are any-of. Both are also reachable as chips,
              and the duplication is deliberate: as a chip, "unsafe structure"
              widens a selection; as a checkbox it restricts one, so
              "milestone-overdue buildings that also carry a lien" needs both
              controls. Neither may be dropped from the page while it is still
              in the URL — a filter the price responds to with nothing on
              screen showing it reads as the price being wrong. */}
          {scope !== "units" && (
            <div className="checkgroup">
              <span className="grouplabel">Only buildings that are</span>
              <div className="checks">
                <label className="check">
                  <input
                    type="checkbox"
                    checked={chosen.milestone_only}
                    onChange={(event) =>
                      set("milestone_only", event.target.checked)
                    }
                  />
                  <span>Milestone overdue</span>
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={chosen.unsafe_only}
                    onChange={(event) =>
                      set("unsafe_only", event.target.checked)
                    }
                  />
                  <span>Carrying an unsafe-structure case</span>
                </label>
              </div>
            </div>
          )}
        </div>

        <Signals
          scope={scope}
          chosen={chosen.signals}
          countySignals={entry?.signals}
          countyName={entry?.name}
          labels={signalLabels}
          onChange={(next) => set("signals", next)}
        />

        <ScoreLegend rules={rules} scope={scope} />
      </section>

      <section className="reportmain">
        {missing ? (
          <p className="empty">{missing}</p>
        ) : (
          <>
            <Preview params={toQuery(settled)} paramKey={key} />

            {/* The price after the document, not before it. A total above
                something the reader has not seen yet is a number with nothing
                to weigh it against. */}
            <Basket quote={quote} error={quoteError} />

            <div className="grp actions">
              <button
                className="btn"
                disabled={!readiness.allowed || generating}
                title={readiness.reason || undefined}
                onClick={() => void generate()}
              >
                {/* What the click actually does, which is not always a
                    purchase: an allowance covering the whole basket and an
                    internal account both build the document and charge
                    nothing, and only an amount due goes to checkout. Keyed on
                    `due_cents` rather than on `waived` for that reason — a
                    subscriber whose plan covered it is not buying anything. */}
                {generating
                  ? "Generating…"
                  : quote && quote.due_cents > 0
                    ? `Buy this report — ${money(quote.due_cents)}`
                    : "Generate this report"}
              </button>
              {/* The reason in text as well as the title: a disabled button
                  whose explanation lives only in a tooltip is unreachable by
                  touch and by keyboard. FilterRail's rule. */}
              {readiness.reason && <p className="sub">{readiness.reason}</p>}
              {generateError && <p className="err">{generateError}</p>}
              {note && <p className="sub ok">{note}</p>}
            </div>
          </>
        )}
      </section>

      <Archive
        reports={archive}
        error={archiveError}
        onChanged={() => void loadArchive()}
      />

      {user && !user.capabilities.report_credits && (
        <p className="sub">
          A paid plan includes report records each month. Yours includes none.
        </p>
      )}
    </main>
  );
}

/**
 * The price, itemised, exactly as the server computed it.
 *
 * Exported so it can be rendered to static markup in a test — `AdminConsole`'s
 * arrangement, and for its reason: this is the screen where somebody decides to
 * spend money, so what it *says* is worth pinning, and it is pure over its
 * props precisely so that is possible without a browser.
 */
export function Basket({ quote, error }: { quote: Quote | null; error: string }) {
  if (error) return <p className="err">{error}</p>;
  if (!quote) return <p className="sub">Pricing this…</p>;

  return (
    <section className="basketbox">
      <h2>
        {quote.county_name} County — {quote.describes}
      </h2>
      <table className="basket">
        <thead>
          <tr>
            <th>Records</th>
            <th className="num">Each</th>
            <th className="num">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {quote.lines.map((line) => (
            <tr key={line.kind}>
              <td>
                {lineSummary(line)}
                {/* What the line is, in the buyer's terms — why every building
                    is charged for, or which apartments sit above the cap and
                    are not. Shown rather than left to be inferred from a
                    number smaller than the reader expected. */}
                {line.note && <div className="sub tiny">{line.note}</div>}
              </td>
              <td className="num">{money(line.unit_cents)}</td>
              <td className="num">{money(line.subtotal_cents)}</td>
            </tr>
          ))}
          {quote.lines.length === 0 && (
            <tr>
              <td colSpan={3} className="sub">
                Nothing matches these filters yet.
              </td>
            </tr>
          )}
        </tbody>
        {quote.lines.length > 0 && (
          <tfoot>
            {/* The document fee is a row rather than an adjustment, because it
                is a charge and every other row in this foot takes something
                off. */}
            {quote.document_fee_cents > 0 && (
              <tr>
                <td colSpan={2}>
                  Report fee
                  <div className="sub tiny">
                    Charged once per report, whatever is in it — and not at all
                    where your plan covers every record.
                  </div>
                </td>
                <td className="num">{money(quote.document_fee_cents)}</td>
              </tr>
            )}
            {/* The ceiling, as its own row: a total silently reduced is a sum
                that does not come out, which is the rule every line follows.
                It is also where somebody should be told a subscription is the
                cheaper purchase. */}
            {quote.ceiling_discount_cents > 0 && (
              <tr>
                <td colSpan={2}>{ceilingNote(quote)}</td>
                <td className="num">−{money(quote.ceiling_discount_cents)}</td>
              </tr>
            )}
            {quote.credits_applied > 0 && (
              <tr>
                <td colSpan={2}>
                  Included in your plan (
                  {quote.credits_applied.toLocaleString()} of{" "}
                  {quote.credits_available.toLocaleString()} records)
                </td>
                <td className="num">−{money(quote.credit_value_cents)}</td>
              </tr>
            )}
            {/* An internal account keeps the full itemisation above and pays
                nothing, so the waiver is its own line rather than a silent
                zero in the total. */}
            {quote.waived && (
              <tr>
                <td colSpan={2}>No charge on this account</td>
                <td className="num">−{money(quote.subtotal_cents)}</td>
              </tr>
            )}
            <tr className="total">
              <td colSpan={2}>
                <b>Total</b>
              </td>
              <td className="num">
                <b>{money(quote.due_cents)}</b>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="sub">{dueSummary(quote)}</p>
      {/* The depth note says what this county evidences; the guide beside it
          says what that does to the rates — including that a feed which has
          stopped being written to does not raise the price. A rule only the
          seller can read is one the buyer has to take on trust, and this is
          the screen where they decide to pay. */}
      <p className="sub depth">
        {depthNote(quote)} <Link to="/pricing">How reports are priced</Link>
      </p>
    </section>
  );
}

/**
 * The document itself, before anybody pays for it.
 *
 * Framed as a **sandboxed blob** rather than pointed at the endpoint: the
 * session token rides in a header and an iframe `src` cannot carry one. The
 * sandbox is the better half of that accident — the preview is our own inert
 * HTML, and rendering it without `allow-same-origin` or `allow-scripts` means
 * it cannot reach this page's storage even if that ever stops being true.
 *
 * The URL is revoked when it changes and when the page closes. A blob URL is a
 * document held in memory until something lets go of it.
 */
function Preview({
  params,
  paramKey,
}: {
  params: Record<string, string | number | boolean>;
  paramKey: string;
}) {
  const [src, setSrc] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const current = useRef("");

  useEffect(() => {
    const controller = new AbortController();
    let url = "";
    setLoading(true);
    setError("");
    api
      .reportPreview(params, controller.signal)
      .then((html) => {
        url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        if (current.current) URL.revokeObjectURL(current.current);
        current.current = url;
        setSrc(url);
      })
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setSrc("");
        setError(
          cause instanceof ApiError
            ? cause.message
            : "Couldn't render a preview.",
        );
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramKey]);

  useEffect(
    () => () => {
      if (current.current) URL.revokeObjectURL(current.current);
    },
    [],
  );

  return (
    <section className="previewbox">
      <div className="previewhead">
        <h2>Preview</h2>
        <span className="sub tiny">
          The document itself, capped for the screen. The report contains every
          matching record.
        </span>
      </div>
      {error && <p className="err">{error}</p>}
      {loading && !error && <p className="sub">Rendering…</p>}
      {src && !error && (
        <iframe
          className="previewframe"
          title="Report preview"
          src={src}
          sandbox=""
        />
      )}
    </section>
  );
}

/**
 * Every report this account has generated.
 *
 * Exported for `Basket`'s reason: what this says about a document that cannot
 * be produced again is worth pinning.
 *
 * The rows are the only copy. `units` and `buildings` are rewritten from the
 * county every morning; these are snapshots of registers that have since moved,
 * so deleting one destroys something no pull can rebuild — which is why the
 * control confirms and says so.
 */
export function Archive({
  reports,
  error,
  onChanged,
}: {
  reports: ReportRecord[] | null;
  error: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [failure, setFailure] = useState("");

  /**
   * Go and pay for a report that is built and waiting.
   *
   * The way back from a checkout somebody cancelled or walked away from —
   * without it a back-button leaves a document nobody can buy until the sweep
   * takes it. The link is minted here rather than carried on the row, because
   * a Checkout Session expires: a URL stored when the report was made and
   * re-listed hours later is a dead link dressed as a button.
   *
   * `busy` is left set on the redirect branch, as `generate` does: the page is
   * navigating away, and re-enabling would invite a second click.
   */
  async function pay(report: ReportRecord) {
    setBusy(report.id);
    setFailure("");
    try {
      const held = await api.payForReport(report.id);
      if (held.checkout_url) {
        track({ name: "begin_checkout", tier: "report" });
        window.location.href = held.checkout_url;
        return;
      }
      // No URL and no error means the server considers it settled. Re-read
      // rather than assert anything about it.
      onChanged();
      setBusy(null);
    } catch (cause) {
      setFailure(
        cause instanceof ApiError ? cause.message : "Couldn't start that payment.",
      );
      setBusy(null);
    }
  }

  async function download(report: ReportRecord) {
    setBusy(report.id);
    setFailure("");
    try {
      const blob = await api.reportPdf(report.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `distress-signal-${report.county_name.toLowerCase()}-${
        report.generated_at.slice(0, 10)
      }-${report.id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setFailure(
        cause instanceof ApiError ? cause.message : "Couldn't fetch that file.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function remove(report: ReportRecord) {
    if (
      !window.confirm(
        `Delete this ${report.county_name} report? The counties have rewritten ` +
          `these registers since it was made, so it cannot be produced again.`,
      )
    ) {
      return;
    }
    setBusy(report.id);
    setFailure("");
    try {
      await api.deleteReport(report.id);
      onChanged();
    } catch (cause) {
      setFailure(
        cause instanceof ApiError ? cause.message : "Couldn't delete that.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="archivebox">
      <h2>Your reports</h2>
      {error && <p className="err">{error}</p>}
      {failure && <p className="err">{failure}</p>}
      {reports === null && <p className="sub">Loading…</p>}
      {reports?.length === 0 && !error && (
        <p className="sub">
          Nothing generated yet. A report you make is kept here so you can
          download it again.
        </p>
      )}
      {reports && reports.length > 0 && (
        <ul className="archivelist">
          {reports.map((report) => (
            <li key={report.id}>
              <div className="archivehead">
                <div>
                  <b>{report.county_name} County</b> — {report.describes}
                  <div className="sub tiny">
                    {report.record_count.toLocaleString()} records ·{" "}
                    {report.building_count.toLocaleString()} buildings ·{" "}
                    {report.unit_count.toLocaleString()} parcels
                  </div>
                </div>
                <div className="archiveprice">
                  {report.waived ? "No charge" : money(report.price_cents)}
                </div>
              </div>
              {/* Two dates, and only the second says anything about the data.
                  Both are printed on the document for that reason, and the
                  list repeats them rather than picking one. */}
              <p className="sub tiny">
                Generated {formatWhen(report.generated_at)} · county data
                pulled{" "}
                {report.pulled_at
                  ? formatWhen(report.pulled_at)
                  : "— no successful pull on record"}
              </p>
              <ArchiveRowActions
                report={report}
                busy={busy === report.id}
                onPay={() => void pay(report)}
                onDownload={() => void download(report)}
                onDelete={() => void remove(report)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What one archived report offers, in the four states it can be in.
 *
 * Split out so the decision is `reportState`'s and this only draws it — the
 * failure it removes is the one `has_pdf` invites: a file *was* stored for a
 * report built for a checkout nobody completed, so a download button drawn off
 * that alone fails every time it is pressed.
 *
 * Every state says something. A row with no control and no sentence reads as a
 * broken page, which is the reason the missing-document case was already
 * written out rather than hidden.
 */
function ArchiveRowActions({
  report,
  busy,
  onPay,
  onDownload,
  onDelete,
}: {
  report: ReportRecord;
  busy: boolean;
  onPay: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const state = reportState(report);
  return (
    <>
      {state.note && (
        <p className={`sub tiny ${state.kind === "held" ? "held" : ""}`}>
          {state.note}
        </p>
      )}
      <div className="archiveactions">
        {state.canDownload && (
          <button className="linkbtn" disabled={busy} onClick={onDownload}>
            {busy ? "Fetching…" : "Download PDF"}
          </button>
        )}
        {state.canPay && (
          <button className="linkbtn pay" disabled={busy} onClick={onPay}>
            {busy ? "Opening checkout…" : state.payLabel}
          </button>
        )}
        {/* Available in every state, including held: a buyer who changed their
            mind should not have to wait out the window to be rid of it. */}
        <button className="linkbtn danger" disabled={busy} onClick={onDelete}>
          Delete
        </button>
      </div>
    </>
  );
}

/**
 * What the score in this report is built from.
 *
 * Asked for so a reader knows exactly what a number in the document means,
 * and rendered from `/api/scoring` rather than from anything written here —
 * a legend holding its own copy of a weight could disagree with the score
 * printed beside it, which is worse than no legend at all.
 *
 * It follows the scope rather than showing both tables, because a building's
 * score is not built from the unit weights: a lien is 30 to a parcel and 25 to
 * a building, seven of the twelve unit signals do not move a building at all,
 * and the building terms are counted *per case*. That last one is why the
 * qualifier is on the row rather than in a footnote — read as a flat 40, a
 * building scoring 934 looks like an error instead of nine unsafe-structure
 * cases. See lib/scoreLegend.ts.
 */
export function ScoreLegend({
  rules,
  scope,
}: {
  rules: ScoringRules | null;
  scope: ReportScope;
}) {
  const sections = scoreLegend(rules, scope);
  if (sections.length === 0) return null;

  return (
    <div className="scorelegend">
      {sections.map((section) => (
        <div className="legendgroup" key={section.scope}>
          <div className="signalhead">
            <span className="eyebrow">{section.heading}</span>
            <span className="sub tiny">{section.note}</span>
          </div>
          <ul className="legendrows">
            {section.rows.map((row) => (
              <li key={row.key}>
                <span className="legendlabel">{row.label}</span>
                <span className="legendpoints">
                  +{row.points}
                  {row.qualifier && (
                    <span className="legendqualifier"> {row.qualifier}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {/* The caveat the scoring endpoint ships with every breakdown. A points
          table invites the reader to treat the number as a measurement, and it
          is a ranking device — the methodology says so at length and this is
          the one line of it that has to travel with the numbers. */}
      <p className="sub tiny">
        {rules?.caveat}{" "}
        <a href="/methodology">How the score works</a>
      </p>
    </div>
  );
}

/**
 * Which signals the report is narrowed to.
 *
 * A multi-select of chips rather than a `<select multiple>`, because the two
 * things that decide whether an option is offered — what the county evidences,
 * and what the scope can filter on — both need a *reason* attached, and a
 * native multi-select has nowhere to put one. Twelve chips also show the whole
 * vocabulary at once, which is the point: a subscriber comparing counties
 * should see that Pasco offers one of these and Miami-Dade twelve.
 *
 * Any-of, said out loud. A reader ticking two boxes could reasonably expect
 * either meaning, and the difference is the whole result — so the sentence
 * under the chips states it rather than leaving it to be discovered.
 */
export function Signals({
  scope,
  chosen,
  countySignals,
  countyName,
  labels,
  onChange,
}: {
  scope: ReportScope;
  chosen: string[];
  countySignals?: string[];
  countyName?: string;
  labels: Record<string, string>;
  onChange: (next: string[]) => void;
}) {
  const options = signalOptions(scope, countySignals, labels, countyName);
  if (options.length === 0) return null;

  const refusals = [
    ...new Set(options.filter((o) => o.disabled && o.reason).map((o) => o.reason)),
  ];

  function toggle(signal: string) {
    onChange(
      chosen.includes(signal)
        ? chosen.filter((name) => name !== signal)
        : [...chosen, signal],
    );
  }

  return (
    <div className="signalpick">
      <div className="signalhead">
        <span className="eyebrow">Signals</span>
        <span className="sub tiny">
          {chosen.length === 0
            ? "Every record, whether or not it carries a signal."
            : `Records carrying ${
                chosen.length === 1 ? "this signal" : "any of these"
              }.`}
        </span>
        {chosen.length > 0 && (
          <button className="linkbtn" onClick={() => onChange([])}>
            Clear
          </button>
        )}
      </div>

      <div className="signalchips">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="signalchip"
            aria-pressed={chosen.includes(option.value)}
            disabled={option.disabled}
            title={option.reason || undefined}
            onClick={() => toggle(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* The reasons in text as well as in each title: a disabled control whose
          explanation lives only in a tooltip is unreachable by touch and by
          keyboard. De-duplicated, because seven chips refused for one reason
          should say it once. */}
      {refusals.map((reason) => (
        <p className="sub tiny" key={reason}>
          {reason}
        </p>
      ))}
    </div>
  );
}
